import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ROUTES } from '@/app/routes/enums';
import { PolicyDocumentViewer } from '@/policy/component/PolicyDocumentViewer';
import { SectionRenderer } from '@/policy/component/SectionRenderer/SectionRenderer';
import { PolicyStatusBar } from '@/policy/component/StatusBar/PolicyStatusBar';
import { useValidation } from '@/policy/contexts';
import { usePausePolicy } from '@/policy/hooks/usePausePolicy';
import { usePolicyCoverageNavigation } from '@/policy/hooks/usePolicyCoverageNavigation';
import { useStartPolicy } from '@/policy/hooks/useStartPolicy';
import { PolicyExtractedData } from '@/policy/types/PolicyCoverage';
import { flattenSections } from '@/policy/utils/flattenSections';

interface PolicyDetailPageContentProps {
  uuid: string;
  policy: PolicyExtractedData;
  pdfBlob: Blob;
}

export const PolicyDetailPageContent = ({
  uuid,
  policy,
  pdfBlob,
}: PolicyDetailPageContentProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { validations } = useValidation();
  const { pausePolicy } = usePausePolicy();

  const validationsRef = useRef(validations);

  useEffect(() => {
    validationsRef.current = validations;
  }, [validations]);

  const sections = policy?.extractedData
    ? flattenSections(policy, t('policy.carGroupTitle'))
    : [];

  const { selectedId, select, advanceFrom } =
    usePolicyCoverageNavigation(sections);

  const policySuccessfullyStarted = useRef(false);
  const policyCompleted = useRef(false);

  const onStartPolicyError = () => {
    policySuccessfullyStarted.current = false;
    navigate(ROUTES.POLICY_LIST, { state: { showStartPolicyError: true } });
  };

  const onStartPolicySuccess = () => {
    policySuccessfullyStarted.current = true;
  };

  const { start } = useStartPolicy({
    uuid,
    onError: onStartPolicyError,
    onSuccess: onStartPolicySuccess,
  });

  const policyStarted = useRef(false);

  useEffect(() => {
    if (!policyStarted.current) {
      policyStarted.current = true;
      start();
    }

    const handleBeforeUnload = () => {
      if (policySuccessfullyStarted.current && !policyCompleted.current) {
        pausePolicy({ uuid, validations: validationsRef.current });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);

      if (policySuccessfullyStarted.current && !policyCompleted.current) {
        pausePolicy({ uuid, validations: validationsRef.current });
      }
    };
  }, [start, pausePolicy, uuid]);

  const boundingBoxes = sections.flatMap((section) =>
    section.overlayRectangles.map((rect) => ({
      ...rect,
      sectionUuid: section.identifier,
    })),
  );

  // Count validation slots per section: car warranties create 2 (carlist + car detail), others create 1
  // TODO : Review count logic
  const extractedSectionsCount = sections.reduce((count, section) => {
    if (section.kind === 'car') {
      return count + 2;
    }
    return count + 1;
  }, 0);
  const missingSectionsCount = validations.missingSections?.length ?? 0;
  const uniqueSectionsCount = extractedSectionsCount + missingSectionsCount;

  return (
    <>
      <div className="flex flex-col min-[1200px]:flex-row h-full w-full min-h-0 pb-14">
        <div className="w-full min-[1200px]:w-1/2 min-[1200px]:min-w-[50%] h-[50vh] min-[1200px]:h-full min-h-0 bg-muted">
          <PolicyDocumentViewer
            blob={pdfBlob}
            documentId={policy.attachmentId}
            boundingBoxes={boundingBoxes}
            className="h-full"
            onRectangleClick={select}
            selectedSectionUuid={selectedId}
          />
        </div>
        <div className="w-full min-[1200px]:w-1/2 min-[1200px]:min-w-[50%] h-[50vh] min-[1200px]:h-full min-h-0 border-t min-[1200px]:border-t-0 min-[1200px]:border-l-2 border-warm-gray-200 overflow-hidden">
          <SectionRenderer
            sections={sections}
            selectedSectionUuid={selectedId}
            onSelectionChanged={select}
            onAdvance={(id) => advanceFrom(id)}
          />
        </div>
      </div>
      <PolicyStatusBar
        uuid={uuid}
        totalSections={uniqueSectionsCount}
        onSuccess={() => {
          policyCompleted.current = true;
          navigate(ROUTES.POLICY_LIST, {
            state: { submittedPolicyNumber: policy.policyNumber },
          });
        }}
        onSaveAndClose={() => {
          policyCompleted.current = true;
          navigate(ROUTES.POLICY_LIST);
        }}
      />
    </>
  );
};
